import { useState, useEffect, useRef, useMemo } from 'react';
import { SignedIn, useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import QRCodeStyling from 'qr-code-styling';

export default function MyPurchases() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const qrRefs = useRef({});

  const [emailClaimMissing, setEmailClaimMissing] = useState(false);
  const [emailClaimHint, setEmailClaimHint] = useState(null);
  const [copiedToken, setCopiedToken] = useState(null);

  // Group purchases by pickup token (one QR per order/checkout); purchases without token stay as single cards
  const orderGroups = useMemo(() => {
    const byToken = {};
    purchases.forEach((p) => {
      const t = p.pickupToken ?? null;
      if (t == null) return;
      const key = String(t);
      if (!byToken[key]) byToken[key] = { token: t, purchases: [] };
      byToken[key].purchases.push(p);
    });
    return Object.values(byToken).map((g) => ({ ...g, purchases: g.purchases.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) }));
  }, [purchases]);
  const orphanPurchases = useMemo(() => purchases.filter((p) => !p.pickupToken), [purchases]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setEmailClaimMissing(false);
      setEmailClaimHint(null);
      try {
        const res = await api('/api/sales/buyer', { getToken });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.error || `Error ${res.status}`);
        }
        // Backend returns { purchases, emailUnavailable } or legacy array
        const list = Array.isArray(data) ? data : (data.purchases ?? []);
        if (!cancelled) {
          setPurchases(list);
          setEmailClaimMissing(data.emailUnavailable === true);
          setEmailClaimHint(data.hint || null);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getToken]);

  // Generate QR codes: one per order group (pickup URL so vendor can open pickup view)
  useEffect(() => {
    orderGroups.forEach((group) => {
      const key = `token-${group.token}`;
      if (qrRefs.current[key]) return;
      const qrContainer = document.getElementById(`qr-pickup-${group.token}`);
      if (!qrContainer) return;
      const pickupUrl = `${window.location.origin}/pickup?t=${encodeURIComponent(group.token)}`;
      const qrCode = new QRCodeStyling({
        width: 200,
        height: 200,
        type: 'svg',
        data: pickupUrl,
        dotsOptions: { color: '#1e293b', type: 'rounded' },
        backgroundOptions: { color: '#ffffff' },
        cornersSquareOptions: { color: '#3730a3', type: 'extra-rounded' },
        cornersDotOptions: { color: '#4f46e5', type: 'dot' },
      });
      qrRefs.current[key] = qrCode;
      qrCode.append(qrContainer);
    });
  }, [orderGroups]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'PickedUp':
        return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200';
      case 'PendingPickup':
        return 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200';
      case 'Paid':
      default:
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'PickedUp':
        return 'Picked Up';
      case 'PendingPickup':
        return 'Pending Pickup';
      case 'Paid':
      default:
        return 'Paid - Ready for Pickup';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-2xl text-slate-500 dark:text-slate-400">Loading purchases…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-3xl font-semibold">My Purchases</h1>
        <p className="rounded-xl bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 text-lg">
          {error}
        </p>
      </div>
    );
  }

  if (emailClaimMissing) {
    return (
      <SignedIn>
        <div className="flex flex-col gap-6">
          <h1 className="text-3xl font-semibold">My Purchases</h1>
          <div className="rounded-xl border-2 border-amber-300 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/20 p-6">
            <p className="text-lg font-medium text-amber-900 dark:text-amber-100 mb-2">
              We couldn’t load your purchases for this account.
            </p>
            <p className="text-slate-700 dark:text-slate-300 mb-2">
              Try signing out and back in. If the problem continues, contact support.
            </p>
            {emailClaimHint && (
              <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">{emailClaimHint}</p>
            )}
          </div>
        </div>
      </SignedIn>
    );
  }

  return (
    <SignedIn>
      <div className="flex flex-col gap-6">
        <h1 className="text-3xl font-semibold">My Purchases</h1>

        {purchases.length === 0 ? (
          <div className="rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 p-8 text-center">
            <p className="text-xl text-slate-600 dark:text-slate-400">
              You haven't made any purchases yet.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            {/* Order groups: one card per checkout, one QR opens vendor pickup view with full cart */}
            {orderGroups.map((group) => {
              const hasPending = group.purchases.some((p) => p.status !== 'PickedUp');
              const first = group.purchases[0];
              const orderTotal = group.purchases.reduce((sum, p) => sum + parseFloat(p.amount), 0);
              return (
                <div
                  key={group.token}
                  className="rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 overflow-hidden"
                >
                  <div className="p-6 flex flex-col gap-4">
                    <h3 className="text-lg font-semibold">
                      {first.vendorName
                        ? `Pickup at ${first.vendorName}`
                        : `Order (${group.purchases.length} item${group.purchases.length !== 1 ? 's' : ''})`}
                    </h3>
                    {(first.vendorLocation || first.marketName) && (
                      <p className="text-sm text-slate-600 dark:text-slate-400 -mt-2">
                        {[first.vendorLocation ? `Booth ${first.vendorLocation}` : null, first.marketName]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    )}
                    <ul className="space-y-2">
                      {group.purchases.map((p) => (
                        <li key={p.id} className="flex justify-between items-center">
                          <span className="font-medium">{p.itemTitle}</span>
                          <span className="text-indigo-600 dark:text-indigo-400">${parseFloat(p.amount).toFixed(2)}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="py-3 border-t border-slate-200 dark:border-slate-700 flex justify-between items-center bg-slate-50 dark:bg-slate-800/80 -mx-6 px-6 rounded-b-lg">
                      <span className="text-lg font-semibold text-slate-700 dark:text-slate-300">Order total</span>
                      <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">${orderTotal.toFixed(2)}</span>
                    </div>
                    <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                      <h3 className="text-lg font-semibold mb-2">Pickup Information</h3>
                      <div className="flex flex-col gap-2 text-slate-700 dark:text-slate-300">
                        <div>
                          <span className="font-medium">Pickup Items: </span>
                          {group.purchases.every((p) => p.status === 'PickedUp')
                            ? 'Picked up'
                            : 'waiting to pick up'}
                        </div>
                        {(first.vendorLocation != null && first.vendorLocation !== '') && (
                          <div>
                            <span className="font-medium">Booth location: </span>
                            {first.vendorLocation}
                          </div>
                        )}
                        {first.vendorName && (
                          <div>
                            <span className="font-medium">Vendor: </span>
                            <button type="button" onClick={() => navigate(`/vendor/${first.vendorId}`)} className="text-indigo-600 dark:text-indigo-400 hover:underline">
                              {first.vendorName}
                            </button>
                          </div>
                        )}
                        {first.marketName && <div><span className="font-medium">Market: </span>{first.marketName}</div>}
                        {(first.marketId != null || (first.marketLocation != null && first.marketLocation.trim() !== '')) && (
                          <button
                            type="button"
                            onClick={() => {
                              const address = first.marketLocation?.trim();
                              if (address) {
                                window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank', 'noopener,noreferrer');
                              } else if (first.marketId != null) {
                                navigate(`/market/${first.marketId}`);
                              }
                            }}
                            className="mt-2 w-full py-3 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 active:bg-indigo-800 transition-colors"
                          >
                            Navigate
                          </button>
                        )}
                      </div>
                    </div>
                    {hasPending && (
                      <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                        <div className="rounded-xl bg-indigo-50 dark:bg-indigo-950/40 border-2 border-indigo-200 dark:border-indigo-700 p-4">
                          <h3 className="text-lg font-semibold mb-1 text-indigo-900 dark:text-indigo-100">
                            {first.vendorName
                              ? `Show this QR only to ${first.vendorName}`
                              : 'Show this QR at the booth'}
                          </h3>
                          <p className="text-sm text-indigo-800/80 dark:text-indigo-200/80 mb-4">
                            Hand your phone to the vendor (or let them scan). They confirm each item as picked up.
                          </p>
                          <div className="flex flex-col items-center gap-3">
                            <div
                              id={`qr-pickup-${group.token}`}
                              className="flex justify-center bg-white p-3 rounded-xl border-2 border-indigo-300 dark:border-indigo-600 shadow-sm"
                              style={{ width: '220px', height: '220px', minHeight: '220px' }}
                            />
                            <button
                              type="button"
                              onClick={async () => {
                                const url = `${window.location.origin}/pickup?t=${encodeURIComponent(group.token)}`;
                                try {
                                  await navigator.clipboard.writeText(url);
                                  setCopiedToken(group.token);
                                  setTimeout(() => setCopiedToken(null), 2000);
                                } catch (_) {}
                              }}
                              className="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
                            >
                              {copiedToken === group.token ? 'Copied!' : 'Copy pickup link (PC demo)'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Older purchases without pickup token: single card each, no QR */}
            {orphanPurchases.map((purchase) => (
              <div
                key={purchase.id}
                className="rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 overflow-hidden"
              >
                <div className="p-6 flex flex-col gap-4">
                  <div className="flex gap-4">
                    {purchase.itemImageUrl && (
                      <img src={purchase.itemImageUrl} alt={purchase.itemTitle} className="w-24 h-24 rounded-lg object-contain flex-shrink-0 bg-slate-100 dark:bg-slate-800" />
                    )}
                    <div className="flex-1 flex flex-col gap-2">
                      <h2 className="text-2xl font-semibold">{purchase.itemTitle}</h2>
                      <div className="flex items-center gap-4">
                        <span className="text-xl font-bold text-indigo-600 dark:text-indigo-400">${parseFloat(purchase.amount).toFixed(2)}</span>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(purchase.status)}`}>{getStatusLabel(purchase.status)}</span>
                      </div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Purchased on {new Date(purchase.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                    <h3 className="text-lg font-semibold mb-2">Pickup Information</h3>
                    <div className="flex flex-col gap-2 text-slate-700 dark:text-slate-300">
                      <div>
                        <span className="font-medium">Pickup Items: </span>
                        {purchase.status === 'PickedUp' ? 'Picked up' : 'waiting to pick up'}
                      </div>
                      {(purchase.vendorLocation != null && purchase.vendorLocation !== '') && (
                        <div>
                          <span className="font-medium">Booth location: </span>
                          {purchase.vendorLocation}
                        </div>
                      )}
                      {purchase.vendorName && (
                        <div>
                          <span className="font-medium">Vendor: </span>
                          <button type="button" onClick={() => navigate(`/vendor/${purchase.vendorId}`)} className="text-indigo-600 dark:text-indigo-400 hover:underline">{purchase.vendorName}</button>
                        </div>
                      )}
                      {purchase.marketName && <div><span className="font-medium">Market: </span>{purchase.marketName}</div>}
                      {(purchase.marketId != null || (purchase.marketLocation != null && purchase.marketLocation.trim() !== '')) && (
                        <button
                          type="button"
                          onClick={() => {
                            const address = purchase.marketLocation?.trim();
                            if (address) {
                              window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank', 'noopener,noreferrer');
                            } else if (purchase.marketId != null) {
                              navigate(`/market/${purchase.marketId}`);
                            }
                          }}
                          className="mt-2 w-full py-3 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 active:bg-indigo-800 transition-colors"
                        >
                          Navigate
                        </button>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">Show purchase #{(purchase.id)} to the vendor for pickup.</p>
                  </div>
                  <button type="button" onClick={() => navigate(`/item/${purchase.itemId}`)} className="w-full py-3 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 active:bg-indigo-800 transition-colors">
                    View Item Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </SignedIn>
  );
}
